export type SaveImageResult = 'saved' | 'shared' | 'downloaded' | 'cancelled';

// Chrome keeps transient user activation for about 5s; stay well inside that.
const TYPE_WAIT_MS = 1000;

const EXTENSION_BY_MIME: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/avif': 'avif',
  'image/bmp': 'bmp',
  'image/x-ms-bmp': 'bmp',
};

const MIME_BY_EXTENSION: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
  avif: 'image/avif',
  bmp: 'image/bmp',
};

export function getExtensionFromMime(mime: string): string {
  return EXTENSION_BY_MIME[mime.split(';')[0].trim().toLowerCase()] ?? '';
}

/** Blob URLs carry no extension; use the blob's own type for those instead. */
export function inferExtension(url: string): string {
  if (url.startsWith('data:')) {
    const mime = /^data:([^;,]+)[;,]/i.exec(url)?.[1];
    return mime ? getExtensionFromMime(mime) : '';
  }

  if (url.startsWith('blob:')) return '';

  const cleanUrl = url.split('?')[0].split('#')[0];
  const ext = /\.([a-z0-9]+)$/i.exec(cleanUrl)?.[1]?.toLowerCase();
  if (!ext) return '';
  const normalized = ext === 'jpeg' ? 'jpg' : ext;
  return normalized in MIME_BY_EXTENSION ? normalized : '';
}

export function buildImageFileName(extension: string, date: Date = new Date()): string {
  const timestamp = date.toISOString().replace(/[:.]/g, '-').split('.')[0];
  return extension ? `image-${timestamp}.${extension}` : `image-${timestamp}`;
}

export async function resolveImageBlob(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load image (${response.status})`);
  }
  return response.blob();
}

export function downloadViaAnchor(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    // Deferred so the browser has started the download before the URL goes away.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}

function pickSaveHandle(fileName: string, mimeHint = '') {
  const dot = fileName.lastIndexOf('.');
  const extension = dot > 0 ? fileName.slice(dot + 1).toLowerCase() : '';
  const mime = mimeHint || MIME_BY_EXTENSION[extension] || '';

  return globalThis.showSaveFilePicker({
    suggestedName: fileName,
    ...(mime && extension ? { types: [{ accept: { [mime]: [`.${extension}`] } }] } : {}),
    id: 'stylegen-image-save',
    startIn: 'downloads',
  });
}

async function writeBlob(handle: StyleGenSaveFileHandle, blob: Blob): Promise<void> {
  const writable = await handle.createWritable();
  try {
    await writable.write(blob);
  } finally {
    await writable.close();
  }
}

export async function saveWithFilePicker(blob: Blob, fileName: string): Promise<void> {
  const handle = await pickSaveHandle(fileName, blob.type);
  await writeBlob(handle, blob);
}

export function isTouchDevice(): boolean {
  if (typeof globalThis.matchMedia === 'function' && globalThis.matchMedia('(pointer: coarse)').matches) return true;
  return (globalThis.navigator?.maxTouchPoints ?? 0) > 0;
}

function canUseShareSheet(): boolean {
  return (
    isTouchDevice() &&
    typeof globalThis.navigator?.share === 'function' &&
    typeof globalThis.navigator?.canShare === 'function'
  );
}

/** True when saving lets the user choose a location (Save As dialog or share sheet). */
export function canChooseSaveLocation(): boolean {
  return 'showSaveFilePicker' in globalThis || canUseShareSheet();
}

/** True when the share sheet may be used, so the image File is worth preloading. */
export function shouldPreloadImageFile(): boolean {
  return canUseShareSheet();
}

export async function loadImageFile(url: string): Promise<File> {
  const blob = await resolveImageBlob(url);
  const extension = getExtensionFromMime(blob.type) || inferExtension(url);
  return new File([blob], buildImageFileName(extension), { type: blob.type });
}

export async function shareImageFile(file: File): Promise<void> {
  await navigator.share({ files: [file] });
}

function isAbortError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError';
}

/**
 * Resolves with the image's mime type, or '' once the wait runs out. The wait is capped
 * because the Save As dialog needs a still-valid transient user activation.
 */
async function waitForBlobType(blobPromise: Promise<Blob>): Promise<string> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<string>((resolve) => {
    timer = setTimeout(() => resolve(''), TYPE_WAIT_MS);
  });

  try {
    return await Promise.race([blobPromise.then((blob) => blob.type), timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/** Resolves to the handle, 'cancelled' when the user dismissed the dialog, or undefined when it can't be used. */
async function openSaveDialog(
  url: string,
  blobPromise: Promise<Blob>,
): Promise<StyleGenSaveFileHandle | 'cancelled' | undefined> {
  let extension = inferExtension(url);
  if (!extension) {
    try {
      extension = getExtensionFromMime(await waitForBlobType(blobPromise));
    } catch {
      return undefined; // The download path below reports the load error.
    }
    // A file handle can't be renamed later, so without a type the download names it after loading.
    if (!extension) return undefined;
  }

  try {
    return await pickSaveHandle(buildImageFileName(extension));
  } catch (error) {
    // Only a rejected picker is a user cancellation; later AbortErrors are real failures.
    if (isAbortError(error)) return 'cancelled';
    console.warn('Save As dialog unavailable, falling back to default download:', error);
    return undefined;
  }
}

/** Resolves to 'shared', 'cancelled' when the user closed the sheet, or undefined when it can't be used. */
async function tryShare(url: string, file: File | undefined): Promise<'shared' | 'cancelled' | undefined> {
  if (!file || !canUseShareSheet()) return undefined;

  // Rebuilt so the name carries a fresh timestamp; this must stay synchronous to keep user activation.
  const named = new File([file], buildImageFileName(getExtensionFromMime(file.type) || inferExtension(url)), {
    type: file.type,
  });
  if (!navigator.canShare({ files: [named] })) return undefined;

  try {
    await shareImageFile(named);
    return 'shared';
  } catch (error) {
    if (isAbortError(error)) return 'cancelled';
    console.warn('Share sheet unavailable, falling back to default download:', error);
    return undefined;
  }
}

/**
 * Tries the native Save As dialog, then (touch devices) the share sheet with the preloaded file,
 * then the default browser download. A cancelled dialog or sheet stops silently.
 */
export async function saveImage(url: string, preloadedFile?: File): Promise<SaveImageResult> {
  let blobPromise: Promise<Blob> | undefined;
  const loadBlob = (): Promise<Blob> => {
    if (!blobPromise) {
      blobPromise = resolveImageBlob(url);
      // Avoids an unhandled rejection when the dialog is cancelled before loading ends.
      blobPromise.catch(() => undefined);
    }
    return blobPromise;
  };

  if ('showSaveFilePicker' in globalThis) {
    const pendingBlob = loadBlob();
    // The dialog needs transient user activation, so it opens before a slow image load
    // finishes; only a URL without a known extension has to wait for the blob type.
    const handle = await openSaveDialog(url, pendingBlob);
    if (handle === 'cancelled') return 'cancelled';

    if (handle) {
      try {
        await writeBlob(handle, await pendingBlob);
        return 'saved';
      } catch (error) {
        console.warn('Saving the image failed, falling back to default download:', error);
      }
    }
  }

  const shareResult = await tryShare(url, preloadedFile);
  if (shareResult === 'cancelled') return 'cancelled';
  if (shareResult === 'shared') return 'shared';

  const blob = await loadBlob();
  downloadViaAnchor(blob, buildImageFileName(getExtensionFromMime(blob.type) || inferExtension(url)));
  return 'downloaded';
}
